/**
 * PageLayout — shared page shell providing the dashboard-style top nav,
 * content area, and status bar to all MangoGridStudio pages.
 *
 * Extracted from ModuleDashboardPage to ensure every page has
 * consistent navigation, branding, and dark navy control-room styling.
 *
 * Usage:
 *   <PageLayout title="Grid Twin" subtitle="Real-time digital twin monitoring">
 *     <YourPageContent />
 *   </PageLayout>
 */

import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import styles from './PageLayout.module.css';

// ---------------------------------------------------------------------------
// Logo icon (same as ModuleDashboardPage)
// ---------------------------------------------------------------------------
function MangoGridIcon(): JSX.Element {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <polygon
        points="24,4 42,14 42,34 24,44 6,34 6,14"
        stroke="#f59e0b"
        strokeWidth="2.5"
        fill="rgba(245,158,11,0.12)"
      />
      <line x1="24" y1="4" x2="24" y2="44" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.55" />
      <line x1="6" y1="14" x2="42" y2="34" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.55" />
      <line x1="6" y1="34" x2="42" y2="14" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.55" />
      <circle cx="24" cy="24" r="4" fill="#f59e0b" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface PageLayoutProps {
  /** Page title shown in the header. */
  title: string;
  /** Optional subtitle shown below the title. */
  subtitle?: string;
  /** Optional right-side header content (search bar, controls, etc.). */
  headerRight?: ReactNode;
  /** Whether to show the page header with title. Default: true. */
  showHeader?: boolean;
  /** Whether to show the bottom status bar. Default: true. */
  showStatusBar?: boolean;
  /** Additional CSS class for the content area. */
  contentClassName?: string;
  /** Main page content. */
  children: ReactNode;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function PageLayout({
  title,
  subtitle,
  headerRight,
  showHeader = true,
  showStatusBar = true,
  contentClassName,
  children,
}: PageLayoutProps): JSX.Element {
  return (
    <div className={styles.page}>
      {/* ── Top navigation bar ── */}
      <nav className={styles.topNav} aria-label="Main navigation">
        <Link to="/dashboard" className={styles.navLogo} aria-label="MangoGridStudio home">
          <MangoGridIcon />
          <span className={styles.navWordmark}>MangoGridStudio</span>
        </Link>

        <span className={styles.navSep} aria-hidden="true" />

        <div className={styles.navLinks}>
          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              `${styles.navLink}${isActive ? ` ${styles.navLinkActive}` : ''}`
            }
          >
            Dashboard
          </NavLink>
          <NavLink
            to="/modules/constraint-map"
            className={({ isActive }) =>
              `${styles.navLink}${isActive ? ` ${styles.navLinkActive}` : ''}`
            }
          >
            Network Map
          </NavLink>
          <NavLink
            to="/twin"
            className={({ isActive }) =>
              `${styles.navLink}${isActive ? ` ${styles.navLinkActive}` : ''}`
            }
          >
            Grid Twin
          </NavLink>
          <NavLink
            to="/congestion"
            className={({ isActive }) =>
              `${styles.navLink}${isActive ? ` ${styles.navLinkActive}` : ''}`
            }
          >
            Congestion
          </NavLink>
        </div>
      </nav>

      {/* ── Main content ── */}
      <div className={`${styles.content}${contentClassName ? ` ${contentClassName}` : ''}`}>
        {showHeader && (
          <header className={styles.pageHeader}>
            <div>
              <h1 className={styles.pageTitle}>{title}</h1>
              {subtitle && <p className={styles.pageSubtitle}>{subtitle}</p>}
            </div>
            {headerRight && <div>{headerRight}</div>}
          </header>
        )}

        {children}
      </div>

      {/* ── Status bar ── */}
      {showStatusBar && (
        <footer className={styles.statusBar} aria-label="Platform status">
          <span className={styles.statusItem}>
            <span className={styles.statusDot} aria-hidden="true" />
            Solver online
          </span>
        </footer>
      )}
    </div>
  );
}
